import React, { useState } from "react";
import SiteLayout from "../components/layout/SiteLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../lib/settings";
import { Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { id: "preset_25", amount: 25 },
  { id: "preset_50", amount: 50 },
  { id: "preset_100", amount: 100 },
  { id: "preset_250", amount: 250 },
];

export default function GivePage() {
  const [selected, setSelected] = useState("preset_100");
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ name: "", email: "", note: "" });
  const [loading, setLoading] = useState(false);
  const { settings } = useSettings();

  const onChooseCustom = () => setSelected("custom");

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        donor_name: donor.name || "Anonymous",
        donor_email: donor.email || "",
        note: donor.note || "",
      };
      if (selected === "custom") {
        const amt = parseFloat(custom);
        if (!amt || amt <= 0) {
          toast.error("Please enter an amount greater than $0.");
          setLoading(false);
          return;
        }
        payload.custom_amount = amt;
      } else {
        payload.package_id = selected;
      }
      const { data } = await api.post("/donations/checkout", payload);
      if (data.url) window.location.href = data.url;
      else throw new Error("No checkout URL returned");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  return (
    <SiteLayout>
      <section className="container-x py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">{settings.give_eyebrow}</div>
            <h1 className="display text-5xl lg:text-6xl">{settings.give_headline}</h1>
            <p className="mt-6 text-[hsl(var(--ink-muted))] leading-relaxed">
              {settings.give_intro}
            </p>
            <div className="mt-12 grid grid-cols-1 gap-6 text-sm">
              <div className="border-l-2 border-[hsl(var(--terracotta))] pl-5">
                <div className="display text-xl">{settings.give_fund_1_title}</div>
                <div className="text-[hsl(var(--ink-muted))] mt-1">{settings.give_fund_1_text}</div>
              </div>
              <div className="border-l-2 border-[hsl(var(--moss))] pl-5">
                <div className="display text-xl">{settings.give_fund_2_title}</div>
                <div className="text-[hsl(var(--ink-muted))] mt-1">{settings.give_fund_2_text}</div>
              </div>
              <div className="border-l-2 border-[hsl(var(--ink))] pl-5">
                <div className="display text-xl">{settings.give_fund_3_title}</div>
                <div className="text-[hsl(var(--ink-muted))] mt-1">{settings.give_fund_3_text}</div>
              </div>
            </div>
            <p className="mt-10 text-xs text-[hsl(var(--ink-muted))] leading-relaxed max-w-md">
              {settings.give_disclaimer}
            </p>
          </div>

          {/* Receipt card */}
          <div className="lg:col-span-7">
            <div data-testid="give-card" className="bg-[hsl(var(--sand))] border border-[hsl(var(--line))] p-8 lg:p-12 rounded-sm">
              <div className="flex items-center justify-between border-b border-dashed border-[hsl(var(--line))] pb-6 mb-8">
                <div>
                  <div className="eyebrow">Online Offering</div>
                  <div className="display text-3xl mt-1">A One-Time Gift</div>
                </div>
                <HeartHandshake className="text-[hsl(var(--terracotta))]" size={28} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {PRESETS.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`give-${p.id}`}
                    onClick={() => setSelected(p.id)}
                    className={`lift py-5 rounded-sm border text-center ${
                      selected === p.id
                        ? "border-[hsl(var(--terracotta))] bg-[hsl(var(--linen))]"
                        : "border-[hsl(var(--line))] bg-[hsl(var(--linen))]/60"
                    }`}
                  >
                    <div className="display text-2xl">${p.amount}</div>
                  </button>
                ))}
              </div>

              <div
                className={`p-5 border rounded-sm mb-8 ${
                  selected === "custom" ? "border-[hsl(var(--terracotta))] bg-[hsl(var(--linen))]" : "border-[hsl(var(--line))]"
                }`}
              >
                <div className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Custom amount</div>
                <div className="mt-3 flex items-center gap-3">
                  <span className="display text-3xl">$</span>
                  <input
                    data-testid="give-custom-input"
                    inputMode="decimal"
                    value={custom}
                    onFocus={onChooseCustom}
                    onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ""))}
                    className="display text-3xl bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--terracotta))] focus:outline-none w-full py-1"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Name (optional)</label>
                  <input
                    data-testid="give-name"
                    value={donor.name}
                    onChange={(e) => setDonor({ ...donor, name: e.target.value })}
                    className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Email (optional)</label>
                  <input
                    data-testid="give-email"
                    value={donor.email}
                    onChange={(e) => setDonor({ ...donor, email: e.target.value })}
                    type="email"
                    className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Note (fund, designation)</label>
                  <input
                    data-testid="give-note"
                    value={donor.note}
                    onChange={(e) => setDonor({ ...donor, note: e.target.value })}
                    placeholder="e.g. Mercy Fund, in memory of…"
                    className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
                  />
                </div>
              </div>

              <button
                data-testid="give-submit"
                disabled={loading}
                onClick={submit}
                className="mt-10 lift inline-flex w-full items-center justify-center gap-2 px-7 py-4 rounded-sm bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta-deep))] text-[hsl(var(--linen))] tracking-wide disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Continue to secure checkout
              </button>
              <div className="mt-4 text-xs text-[hsl(var(--ink-muted))] text-center">
                Payments processed securely by Stripe.
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
